import { AggregateScoreType, ExceptionType } from "~/operations";
import {
  AdvisoryOnAssetFindingNode,
  CheckOnAssetFindingNode,
  CveOnAssetFindingNode,
  FindingNode,
  GenericFindingOnAssetFindingNode,
} from "~/pages/findings/FindingOnAsset/types";
import { Stack, Typography } from "@mui/material";
import { DynamicIcon } from "~/components/icons/DynamicIcon";
import { ExceptionTarget } from "~/components/exceptions/types";
import { MONDOO_VULN_MRN_PREFIX } from "../../../../constants";
import { AggregateScoresNode } from "~/components/FirewatchPage";

type FindingUtilsCommonParams = {
  vulnId: string;
  advisoryId: string;
  checkMrn: string;
  genericFindingMrn: string;
};

export function getFindingMrn({
  vulnId,
  advisoryId,
  checkMrn,
  genericFindingMrn,
}: FindingUtilsCommonParams): string {
  if (checkMrn) {
    return checkMrn;
  }

  if (genericFindingMrn) {
    return genericFindingMrn;
  }

  if (vulnId) {
    return `${MONDOO_VULN_MRN_PREFIX}/cves/${vulnId}`;
  }

  if (advisoryId) {
    return `${MONDOO_VULN_MRN_PREFIX}/advisories/${advisoryId}`;
  }

  return "";
}

export function getFindingAggregateScoreType({
  vulnId,
  advisoryId,
  checkMrn,
}: FindingUtilsCommonParams): AggregateScoreType | null {
  if (checkMrn) return AggregateScoreType.Check;

  if (vulnId) return AggregateScoreType.Vulnerability;

  if (advisoryId) return AggregateScoreType.Advisory;

  return null;
}

export function getFindingExceptionTypes({
  vulnId,
  advisoryId,
  checkMrn,
  genericFindingMrn,
}: FindingUtilsCommonParams): ExceptionType[] {
  if (checkMrn) return [ExceptionType.Security];

  if (vulnId) return [ExceptionType.Cve];

  if (advisoryId) return [ExceptionType.Advisory];

  if (genericFindingMrn) return [ExceptionType.Fex];

  return [];
}

type GetFindingExceptionMrnsReturn = {
  advisoryMrns?: Array<string>;
  cveMrns?: Array<string>;
  queryMrns?: Array<string>;
};

export function getFindingExceptionMrns({
  vulnId,
  advisoryId,
  checkMrn,
  genericFindingMrn,
}: FindingUtilsCommonParams): GetFindingExceptionMrnsReturn {
  return {
    ...(advisoryId
      ? {
          advisoryMrns: [
            getFindingMrn({ vulnId, advisoryId, checkMrn, genericFindingMrn }),
          ],
          applyToCves: true,
        }
      : {}),
    ...(vulnId
      ? {
          cveMrns: [
            getFindingMrn({ vulnId, advisoryId, checkMrn, genericFindingMrn }),
          ],
        }
      : {}),
    ...(checkMrn
      ? {
          queryMrns: [
            getFindingMrn({ vulnId, advisoryId, checkMrn, genericFindingMrn }),
          ],
        }
      : {}),
    ...(genericFindingMrn
      ? {
          // it's intended to be advisoryMrns for now.
          advisoryMrns: [
            getFindingMrn({ vulnId, advisoryId, checkMrn, genericFindingMrn }),
          ],
          applyToCves: false,
        }
      : {}),
  };
}

type GetFindingLinkBackParams = FindingUtilsCommonParams & {
  assetId: string;
  jobId: string | undefined;
  projectId: string | undefined;
  scopeParams: URLSearchParams;
};

export function getFindingLinkBack({
  vulnId,
  advisoryId,
  checkMrn,
  genericFindingMrn,
  assetId,
  jobId,
  projectId,
  scopeParams,
}: GetFindingLinkBackParams): string {
  if (vulnId) {
    return jobId && projectId
      ? `/space/cicd/jobs/${projectId}/vulnerabilities?${scopeParams}&projectId=${projectId}&jobId=${jobId}`
      : `/space/inventory/${assetId}/vulnerabilities?${scopeParams}`;
  }

  if (advisoryId) {
    return jobId && projectId
      ? `/space/cicd/jobs/${projectId}/advisories?${scopeParams}&projectId=${projectId}&jobId=${jobId}`
      : `/space/inventory/${assetId}/advisories?${scopeParams}`;
  }

  if (checkMrn) {
    return jobId && projectId
      ? `/space/cicd/jobs/${projectId}/checks?${scopeParams}&projectId=${projectId}&jobId=${jobId}`
      : `/space/inventory/${assetId}/checks?${scopeParams}`;
  }

  if (genericFindingMrn) {
    return jobId && projectId
      ? `/space/cicd/jobs/${projectId}/generic-findings?${scopeParams}&projectId=${projectId}&jobId=${jobId}`
      : `/space/inventory/${assetId}/generic-findings?${scopeParams}`;
  }

  return jobId && projectId
    ? `/space/cicd/jobs/${projectId}/overview?${scopeParams}&projectId=${projectId}&jobId=${jobId}`
    : `/space/inventory/${assetId}/`;
}

type FindingEntityCommonParams = {
  finding: FindingNode | undefined;
};

export function getFindingTitle({
  finding,
}: FindingEntityCommonParams): string {
  if (isAdvisoryOrCveFinding(finding)) return finding?.id;

  if (isCheckFinding(finding) || isGenericFinding(finding))
    return finding?.title;

  return "";
}

export interface FindingSubheader {
  id: string;
  label: string;
  value: string | undefined | null;
  component?: any;
}

export function getFindingSubheaders({
  finding,
}: FindingEntityCommonParams): FindingSubheader[] {
  if (finding?.__typename === "PackageFinding") return [];

  const commonSubheader = {
    id: "platform-title",
    label: "Platform title",
    value: finding?.asset?.platform?.title,
    component: (
      <Stack direction="row" spacing={1}>
        <DynamicIcon iconId={finding?.asset?.icon} />
        <Typography className="info-asset-title" variant="subtitle2">
          {finding?.asset?.platform?.title}
        </Typography>
      </Stack>
    ),
  };

  if (isAdvisoryOrCveFinding(finding)) {
    return [
      commonSubheader,
      {
        id: "first-found",
        label: "First found",
        value: finding?.firstDetectedAt,
      },
      {
        id: "last-confirmed",
        label: "Last confirmed",
        value: finding?.lastUpdated,
      },
    ];
  }

  if (isCheckFinding(finding)) {
    return [
      commonSubheader,
      {
        id: "last-updated-at",
        label: "Last updated at",
        value: finding?.lastUpdated,
      },
    ];
  }

  if (isGenericFinding(finding)) {
    return [
      commonSubheader,
      {
        id: "first-found",
        label: "First found",
        value: finding?.firstDetectedAt,
      },
      {
        id: "last-updated-at",
        label: "Last updated at",
        value: finding?.lastUpdated,
      },
    ];
  }

  return [];
}

export function isAdvisoryFinding(
  finding: FindingEntityCommonParams["finding"] | null | undefined,
): finding is AdvisoryOnAssetFindingNode {
  return finding?.__typename === "AdvisoryFinding";
}

export function isCheckAggregateScore(
  finding: AggregateScoresNode | null | undefined,
): finding is AggregateScoresNode {
  return (
    finding?.__typename === "AggregateScore" &&
    finding?.scoreType === AggregateScoreType.Check
  );
}

export function isAdvisoryOrCveFinding(
  finding: { __typename?: string } | null | undefined,
): finding is AdvisoryOnAssetFindingNode | CveOnAssetFindingNode {
  return (
    finding?.__typename === "AdvisoryFinding" ||
    finding?.__typename === "CveFinding"
  );
}

export function isAdvisoryAggregateScore(
  finding: AggregateScoresNode | null | undefined,
): finding is AggregateScoresNode {
  return (
    finding?.__typename === "AggregateScore" &&
    finding?.scoreType === AggregateScoreType.Advisory
  );
}

export function isVulnerabilityFinding(
  finding: FindingEntityCommonParams["finding"] | null | undefined,
): finding is CveOnAssetFindingNode {
  return finding?.__typename === "CveFinding";
}

export function isCheckFinding(
  finding: FindingEntityCommonParams["finding"] | null | undefined,
): finding is CheckOnAssetFindingNode {
  return finding?.__typename === "CheckFinding";
}

export function isGenericFinding(
  finding: FindingEntityCommonParams["finding"] | null | undefined,
): finding is GenericFindingOnAssetFindingNode {
  return finding?.__typename === "GenericFinding";
}

export function getCheckFindingRemediation({
  finding,
  checkData,
}: FindingEntityCommonParams & {
  checkData: CheckOnAssetFindingNode["mquery"] | undefined;
}) {
  if (isCheckFinding(finding)) {
    const remediations = checkData?.docs?.remediations;
    const hasRemediations =
      remediations?.__typename === "Remediations" &&
      remediations.entries.length > 0;

    return hasRemediations ? remediations : { entries: [] };
  }

  return { entries: [] };
}

export function getFindingExceptionTarget(
  finding: FindingEntityCommonParams["finding"] | undefined,
): ExceptionTarget {
  if (isCheckFinding(finding)) return "check";

  if (isVulnerabilityFinding(finding)) return "cve";

  if (isAdvisoryFinding(finding)) return "advisory";

  return "finding";
}
